import Promise from 'bluebird';
import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';

import { BingMapsContext } from './bing-maps-context';
import { pollForBingMapsSdk } from '../util/poll-for-bing-maps-sdk';
import { BingMapsApiHelper } from '../util/bing-maps-api-helper';

function injectScript(userRegion, locale) {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;

    const url = new URL('https://www.bing.com/api/maps/mapcontrol');
    if (userRegion) {
      url.searchParams.append('UR', userRegion);
    }
    if (locale) {
      url.searchParams.append('setLang', locale);
    }
    script.src = url.toString();
    script.onload = resolve;
    script.onerror = (e) => {
      reject(e);
    };

    // inject new <script> before the first <script> element
    const node = document.getElementsByTagName('script')[0];
    node.parentNode.insertBefore(script, node);
  });
}

/**
 * Provides shared values for initializing Bing Maps related services.
 * @param {Object} props - React Props.
 * @returns {ReactNode} - BingMapsContext provider.
 */
export function BingMapsContextProvider(props) {
  const {
    campaignApiServiceRoot,
    campaignApiToken,
    bingMapsApiKey,
    children,
    searchOptions,
    logActivity,
    instrumentationService,
    userRegion,
    locale,
  } = props;

  const [loadBingMapsSdkRequested, setLoadBingMapsSdkRequested] = useState(false);
  const [Maps, setMaps] = useState(null);
  const [MapsError, setMapsError] = useState(null);
  const [MapsApiHelper, setMapsApiHelper] = useState(null);

  const onSdkLoaded = useCallback((sdk) => {
    // Maps libraries have been loaded, create the API helper class
    setMaps(sdk);
    setMapsApiHelper(new BingMapsApiHelper({ bingMapsApiKey, Maps: sdk }));
  }, [bingMapsApiKey]);

  const loadBingMapsSdk = useCallback(() => {
    if (loadBingMapsSdkRequested) {
      // SDK initialization has already started, nothing to do
      return;
    }

    setLoadBingMapsSdkRequested(true);

    // poll once to check if SDK is already on the page
    pollForBingMapsSdk(1).then((existingSdk) => {
      if (existingSdk) {
        onSdkLoaded(existingSdk);
      } else {
        // asynchronously load the external javascript and then poll until the Map APIs are loaded
        injectScript(userRegion, locale)
          .then(pollForBingMapsSdk)
          .then((newSdk) => {
            onSdkLoaded(newSdk);
          })
          .catch((e) => {
            setMapsError(e);
            instrumentationService.logError(`Failed to load bing map SDK, exception: ${JSON.stringify(e)}`);
          });
      }
    });
  }, [loadBingMapsSdkRequested, onSdkLoaded, userRegion, locale, instrumentationService]);

  const value = useMemo(() => ({
    campaignApiServiceRoot,
    campaignApiToken,
    bingMapsApiKey,
    loadBingMapsSdk,
    loadBingMapsSdkRequested,
    Maps,
    MapsError,
    MapsApiHelper,
    searchOptions,
    logActivity,
    instrumentationService,
  }), [
    campaignApiServiceRoot,
    campaignApiToken,
    bingMapsApiKey,
    loadBingMapsSdk,
    loadBingMapsSdkRequested,
    Maps,
    MapsError,
    MapsApiHelper,
    searchOptions,
    logActivity,
    instrumentationService,
  ]);

  return (
    <BingMapsContext.Provider value={value}>
      {children}
    </BingMapsContext.Provider>
  );
}

BingMapsContextProvider.propTypes = {
  /**
   * Campaign odata API URL.
   */
  campaignApiServiceRoot: PropTypes.string,
  /**
   * CCMTSmallToken used for odata requests.
   */
  campaignApiToken: PropTypes.string,
  /**
   * API key for use with Bing Maps. You can obtain an API key from https://www.bingmapsportal.com/
   */
  bingMapsApiKey: PropTypes.string.isRequired,

  /**
   * Optional parameters to pass to the useBingMapsSearch hook. Use searchOptions.getLocations to
   * override the default odata location search.
   * Use searchOptions.filterSearchResults to apply a filter
   */
  searchOptions: PropTypes.shape({}),
  /**
   * Optional parameter for logging activity
   */
  logActivity: PropTypes.func,
  /**
   * Optional parameter for logging
   */
  instrumentationService: PropTypes.shape({
    logError: PropTypes.func.isRequired,
    logTrace: PropTypes.func.isRequired,
  }),
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]).isRequired,
  userRegion: PropTypes.string,
  locale: PropTypes.string,
};

BingMapsContextProvider.defaultProps = {
  campaignApiServiceRoot: undefined,
  campaignApiToken: undefined,
  searchOptions: undefined,
  logActivity: _.noop,
  instrumentationService: { logError: _.noop, logTrace: _.noop },
  userRegion: undefined,
  locale: undefined,
};
